import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetCondition,
  AssetStatus,
  RepairRequestStatus,
  RepairUrgency,
} from '@cfl/assets-shared';

import { api, API_URL } from '@/lib/api';
import { assetKeys } from '@/lib/assets-api';
import { repairKeys } from '@/lib/operations-api';

export interface RepairRequestAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedBy?: { name: string } | null;
}

export interface RepairRequest {
  id: string;
  asset: { id: string; code: string; name: string; status: AssetStatus };
  reportedBy: { id: string; name: string; employeeCode: string; department: string | null };
  description: string;
  urgency: RepairUrgency;
  status: RepairRequestStatus;
  reviewNote: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  repairId: string | null;
  createdAt: string;
  attachments: RepairRequestAttachment[];

  /**
   * What was already known to be wrong with this machine when THIS reporter received
   * it, frozen at handover.
   *
   * Null when no condition was ever recorded, and also null when the asset is no
   * longer theirs - a snapshot belonging to a different holder says nothing about this
   * report, and showing it anyway would look like evidence.
   */
  handover: {
    assignedAt: string;
    condition: AssetCondition;
    faults: string[];
    notes: string | null;
    /** Null means nobody recorded a condition for that handover. NOT the same as
     *  "it went out with nothing wrong". */
    checkedAt: string | null;
  } | null;
}

export const repairRequestKeys = {
  all: ['repair-requests'] as const,
  mine: ['repair-requests', 'mine'] as const,
  queue: (status?: string) => ['repair-requests', 'queue', status ?? 'all'] as const,
};

/** Everyone signed in can read their own, including a plain employee. */
export function useMyRepairRequests() {
  return useQuery({
    queryKey: repairRequestKeys.mine,
    queryFn: () => api.get<RepairRequest[]>('/repair-requests/mine'),
  });
}

export function useRepairRequestQueue(
  status?: RepairRequestStatus | '',
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: repairRequestKeys.queue(status || undefined),
    queryFn: () => api.get<RepairRequest[]>(`/repair-requests${status ? `?status=${status}` : ''}`),
    enabled: options.enabled ?? true,
  });
}

export function useRaiseRepairRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { assetId: string; description: string; urgency: RepairUrgency }) =>
      api.post<{ id: string; assetCode: string }>('/repair-requests', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: repairRequestKeys.all }),
  });
}

export function useAttachToRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<RepairRequestAttachment>(`/repair-requests/${id}/attachments`, form);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: repairRequestKeys.all }),
  });
}

export function useReviewRepairRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      status: 'ACKNOWLEDGED' | 'REPAIR_RAISED' | 'DECLINED' | 'RESOLVED';
      note?: string | null;
      reason?: string | null;
    }) =>
      api.patch<{ id: string; status: RepairRequestStatus; repairId: string | null }>(
        `/repair-requests/${id}/review`,
        input,
      ),
    onSuccess: () => {
      // Raising a repair moves the asset out of service, so the register and the
      // repair list both change - not just the request queue.
      void queryClient.invalidateQueries({ queryKey: repairRequestKeys.all });
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      void queryClient.invalidateQueries({ queryKey: repairKeys.all });
    },
  });
}

/**
 * The URL an attached photo is served from.
 *
 * A plain URL rather than a fetched blob, so `<img src>` works and the browser
 * caches it. It carries the session cookie because the API is same-site and the
 * image request is credentialed by default for same-site subresources.
 */
export function attachmentUrl(attachmentId: string): string {
  return `${API_URL}/repair-requests/attachments/${attachmentId}`;
}
